import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/** What the user picked. `system` follows the OS; light and dark are explicit. */
export type Theme = 'system' | 'light' | 'dark';
/** What is actually painted — `system` resolved against the OS preference. */
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  /** Cycles light → dark → system → light. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = 'nova.theme';

const prefersDark = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolve = (theme: Theme): ResolvedTheme =>
  theme === 'system' ? (prefersDark() ? 'dark' : 'light') : theme;

/**
 * Light is Nova's default; dark is there for dim rooms, and `system` follows the
 * OS. The choice is remembered per browser and applied as `data-theme` on <html>,
 * which is what the palettes in styles.css hang off.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'light';
  });
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolve(theme));

  useEffect(() => {
    const applied = resolve(theme);
    setResolvedTheme(applied);
    document.documentElement.setAttribute('data-theme', applied);
    localStorage.setItem(STORAGE_KEY, theme);

    // While on «system», track the OS preference live.
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const next: ResolvedTheme = mq.matches ? 'dark' : 'light';
      setResolvedTheme(next);
      document.documentElement.setAttribute('data-theme', next);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => setThemeState(next), []);
  const toggleTheme = useCallback(
    () =>
      setThemeState((current) =>
        current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light',
      ),
    [],
  );

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, toggleTheme }),
    [theme, resolvedTheme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme må brukes inne i ThemeProvider.');
  return context;
}

/**
 * Recharts takes colours as plain strings, so it cannot read Tailwind classes.
 * This resolves the same CSS variables the rest of the UI uses, and re-resolves
 * them when the theme changes.
 */
export function useChartTheme() {
  const { resolvedTheme: theme } = useTheme();

  return useMemo(() => {
    const read = (name: string, fallback: string) => {
      if (typeof window === 'undefined') return fallback;
      const value = getComputedStyle(document.documentElement)
        .getPropertyValue(`--nova-${name}`)
        .trim();
      return value || fallback;
    };

    // RGB-channel variables need wrapping; the rest are already colour values.
    const channel = (name: string, fallback: string, alpha = 1) => {
      const value = read(name, '');
      return value ? `rgb(${value} / ${alpha})` : fallback;
    };

    return {
      theme,
      grid: read('chart-grid', 'rgb(139 152 172 / 0.12)'),
      axis: read('chart-axis', 'rgb(139 152 172 / 0.5)'),
      reference: read('chart-reference', 'rgb(139 152 172 / 0.5)'),
      tooltipBackground: read('chart-tooltip-bg', 'rgb(17 24 39 / 0.92)'),
      tooltipBorder: read('hairline', 'rgb(56 189 248 / 0.18)'),
      tooltipText: channel('ink', '#E6EDF7'),
      accent: channel('accent', '#38BDF8'),
      cursor: channel('accent', '#38BDF8', 0.25),
    };
  }, [theme]);
}
